import { useCallback, useEffect, useMemo, useState } from "react";

import {
  getMediaVariantCandidates,
  type MediaUrlVariant,
} from "@/utils/mediaUrlVariants";

interface ImageFallback {
  hasFailed: boolean;
  onError: () => void;
  src: string;
}

export function useImagePreview({
  preferredVariant = "thumbnail",
  url,
}: {
  preferredVariant?: MediaUrlVariant;
  url?: string;
}): ImageFallback {
  const candidates = useMemo(
    () => getMediaVariantCandidates(url ?? "", preferredVariant),
    [preferredVariant, url],
  );

  return useImageFallback(candidates);
}

export function useImageFallback(candidates: string[]): ImageFallback {
  const candidatesKey = candidates.join("\n");
  const [candidateIndex, setCandidateIndex] = useState(0);
  const [hasFailed, setHasFailed] = useState(false);

  useEffect(() => {
    setCandidateIndex(0);
    setHasFailed(false);
  }, [candidatesKey]);

  const onError = useCallback(() => {
    setCandidateIndex((currentIndex) => {
      if (currentIndex >= candidates.length - 1) {
        setHasFailed(true);

        return currentIndex;
      }

      return currentIndex + 1;
    });
  }, [candidates.length]);

  return { hasFailed, onError, src: candidates[candidateIndex] ?? "" };
}
